/// <amd-module name="Artifacts/FeedSettings/components/UpstreamsTab" />

import {
  IUpstreamTableItem,
  renderStatusCell,
} from "Artifacts/FeedSettings/components/UpstreamStatusCell";
import {
  deleteUpstreamSources,
  getUpstreamSourceTypeName,
  moveUpstreamSourceDown,
  moveUpstreamSourceUp,
} from "Artifacts/FeedSettings/helpers/UpstreamHelpers";
import * as Resources from "Artifacts/FeedSettings/Resources";
import { IUpstreamSource } from "Artifacts/FeedSettings/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { GenericModalId, IGenericModalProps } from "Artifacts/Root/components/GenericModal";
import { ComponentState } from "Artifacts/Root/Constants";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { connectDynamicModules } from "Artifacts/Root/Redux";
import { RootActions } from "Artifacts/Root/Redux/Actions";
import { ICurrentModal } from "Artifacts/Root/redux/Contracts";
import { RootModule } from "Artifacts/Root/Redux/Module";
import { IFeed } from "Artifacts/Root/types";
import { UpstreamHealthStatus } from "Artifacts/Root/web-apis/contracts/WebPage";
import * as React from "react";
import { connect } from "react-redux";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import * as StringUtils from "VSS/Core/Util/String";
import { useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Header } from "VSSUI/Header";
import { IListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import {
  ColumnSelect,
  ITableBreakpoint,
  ITableColumn,
  renderSimpleCell,
  SimpleTableCell,
  Table,
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IUpstreamsTabOwnProps {
  feed?: IFeed;
  upstreamList?: IUpstreamSource[];
  upstreamStatuses?: UpstreamHealthStatus[];
  /** The selection object as an observable value doesn't cause a re-render
   * so we're keeping this selectedUpstreams array in the props despite the fact
   * that it looks like it could easily be factored out. Feel free to try to get
   * the <Observer /> component to work, but it was broken at the time of writing */
  selectedUpstreams: IUpstreamSource[];
  selection: IListSelection;
  showAddUpstreamPanel: () => void;
  setUpstreamList: (upstreams: IUpstreamSource[]) => void;
  saveUpstreamConfiguration: () => void;
  componentState: ComponentState;
  saveUpstreamsComponentState: ComponentState;
  resetUpstreamConfigurationComponentState: () => void;
  isUpstreamsDirty: boolean;
  isUserAdmin: boolean;
}

const Actions = {
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

const DeleteUpstreamsModalName = "DeleteUpstreamsModal";

type IUpstreamsTabProps = IUpstreamsTabOwnProps & typeof Actions;

const tableBreakpoints: ITableBreakpoint[] = [
  {
    breakpoint: ScreenBreakpoints.xsmall,
    columnWidths: [10, 40, -20, -30, -25, -25],
  },
  {
    breakpoint: ScreenBreakpoints.small,
    columnWidths: [10, 60, -20, -40, -20, -20],
  },
];

let columns: ITableColumn<unknown>[] = [
  new ColumnSelect(),
  {
    id: "protocol",
    name: Resources.UpstreamsTab_ProtocolColumnName,
    readonly: true,
    renderCell: renderProtocolIconCell,
    width: new ObservableValue(60),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
  {
    id: "source",
    name: Resources.UpstreamsTab_SourceNameColumnName,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-20),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
  {
    id: "location",
    name: Resources.UpstreamsTab_LocationColumnName,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-40),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
  {
    id: "sourceType",
    name: Resources.UpstreamsTab_SourceTypeColumnName,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-20),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
  {
    id: "lastSynced",
    name: Resources.UpstreamsTab_SyncColumnName,
    readonly: true,
    renderCell: renderStatusCell,
    width: new ObservableValue(-20),
    onSize: (event, index, width) => {
      columns = resizeColumn(columns, index, width);
    },
  },
];

const UpstreamsTab: React.FC<IUpstreamsTabProps> = ({
  feed,
  upstreamList,
  upstreamStatuses,
  selectedUpstreams,
  selection,
  showAddUpstreamPanel,
  setUpstreamList,
  saveUpstreamConfiguration,
  showModal,
  hideModal,
  componentState,
  saveUpstreamsComponentState,
  resetUpstreamConfigurationComponentState,
  isUpstreamsDirty,
  isUserAdmin,
}) => {
  const { pageContext } = useComponentContext();
  // Track whether an upstream was deleted so we can warn the user when they save
  const [upstreamsDeleted, setUpstreamsDeleted] = React.useState<boolean>(false);

  const upstreamsItemProvider = React.useMemo(() => {
    const protocolService: IProtocolService = pageContext.getService<IProtocolService>(
      "IProtocolService",
    );
    const items = upstreamList?.map<IUpstreamTableItem>((upstream) => {
      return {
        key: upstream.id,
        protocol: protocolService.getProtocolById(upstream.protocol),
        source: upstream.name,
        location: upstream.displayLocation || upstream.location || "",
        sourceType: getUpstreamSourceTypeName(upstream.upstreamSourceType),
        upstreamSource: upstream,
        status: upstreamStatuses?.find((status) => status.upstreamId === upstream.id),
      };
    });
    return new ArrayItemProvider(items || []);
  }, [pageContext, upstreamList, upstreamStatuses]);

  return (
    <DefaultLoader state={componentState}>
      {isPublicFeed(feed) && (
        <div className="public-feed-message">
          <MessageCard severity={MessageCardSeverity.Warning}>
            <FWLink linkId="2207839">
              {Resources.UpstreamsTab_UpstreamsForPublicFeeds_Prerequisite}
            </FWLink>
          </MessageCard>
        </div>
      )}
      <Card
        className="flex-grow bolt-table-card"
        contentProps={{ contentPadding: false }}
        renderHeader={() => (
          <Header
            // Force all buttons to be displayed when possible
            buttonCount={5}
            title={StringUtils.format(Resources.UpstreamsTab_Title, feed?.name)}
            titleAriaLevel={2}
            commandBarItems={[
              {
                id: "add",
                text: Resources.UpstreamsTab_AddSource,
                iconProps: { iconName: "Add" },
                isPrimary: true,
                onActivate: () => {
                  showAddUpstreamPanel();
                  resetUpstreamConfigurationComponentState();
                },
                disabled: !isUserAdmin,
              },
              // All of these just re-order the upstreams in state
              {
                id: "delete",
                text: Resources.Delete_Label,
                disabled: !isUserAdmin || selectedUpstreams.length < 1,
                iconProps: { iconName: "Delete" },
                onActivate: () => {
                  if (upstreamList) {
                    setUpstreamList(deleteUpstreamSources(selection, upstreamList));
                    setUpstreamsDeleted(true);
                    resetUpstreamConfigurationComponentState();
                  }
                },
              },
              {
                id: "moveUp",
                text: Resources.UpstreamsTab_MoveSourceUp,
                disabled: !isUserAdmin || selectedUpstreams.length !== 1,
                iconProps: { iconName: "Up" },
                onActivate: () => {
                  upstreamList && setUpstreamList(moveUpstreamSourceUp(selection, upstreamList));
                  resetUpstreamConfigurationComponentState();
                },
              },
              {
                id: "moveDown",
                text: Resources.UpstreamsTab_MoveSourceDown,
                disabled: !isUserAdmin || selectedUpstreams.length !== 1,
                iconProps: { iconName: "Down" },
                onActivate: () => {
                  upstreamList && setUpstreamList(moveUpstreamSourceDown(selection, upstreamList));
                  resetUpstreamConfigurationComponentState();
                },
              },
              // And then this one fires the 1 redux action to send the new order/upstreams to the server
              // Prompt the user with a warning modal if they deleted an upstream
              {
                id: "save",
                text: Resources.UpstreamsTab_SaveButton,
                disabled: !isUserAdmin || !isUpstreamsDirty,
                iconProps: isLoading(saveUpstreamsComponentState)
                  ? {
                      render: (className) => (
                        <Spinner className={className} size={SpinnerSize.small} />
                      ),
                    }
                  : { iconName: "Save" },
                onActivate: () => {
                  if (upstreamsDeleted) {
                    const deletedUpstreamModal: ICurrentModal<IGenericModalProps> = {
                      modalId: GenericModalId,
                      props: {
                        title: Resources.UpstreamsTab_Modal_Title,
                        primaryButtonText: Resources.UpstreamsTab_SaveButton,
                        primaryButtonDanger: true,
                        body: Resources.UpstreamsTab_Modal_Body,
                        className: "confirm-upstream-delete-dialog",
                        onPrimaryClicked: () => {
                          saveUpstreamConfiguration();
                          hideModal();
                        },
                        onDismiss: hideModal,
                        primaryButtonComponentState: saveUpstreamsComponentState,
                      },
                    };
                    showModal(deletedUpstreamModal, DeleteUpstreamsModalName);
                  } else {
                    saveUpstreamConfiguration();
                  }
                },
              },
            ]}
          />
        )}
      >
        <Table
          columns={columns}
          itemProvider={upstreamsItemProvider}
          rowHeight={100}
          selection={selection}
          tableBreakpoints={tableBreakpoints}
          selectableText={true}
        />
      </Card>
    </DefaultLoader>
  );
};

function renderProtocolIconCell(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUpstreamTableItem>,
  tableItem: IUpstreamTableItem,
): JSX.Element {
  const protocol = tableItem.protocol;

  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Tooltip text={tableItem.protocol.name}>
        <div>
          <WrappedComponent
            wrappedType={tableItem.protocol.getLogoContributionId()}
            dependencies={[tableItem.protocol.getLogoContributionId()]}
            wrappedProps={{ className: "table-protocol-icon", alt: protocol.name }}
          />
        </div>
      </Tooltip>
    </SimpleTableCell>
  );
}

const ConnectedUpstreamsTab = connectDynamicModules<IUpstreamsTabOwnProps>(
  connect(null, Actions)(UpstreamsTab),
  /** For some reason root module exports a module and not an array of modules */
  [RootModule],
);

export { ConnectedUpstreamsTab as UpstreamsTab };
